export type AddNodeFn = (type: string, options?: Record<string, unknown>) => string;
export type GetViewportFn = () => { x: number; y: number; zoom: number };

export type SetCenterFn = (
  x: number,
  y: number,
  opts?: { zoom?: number; duration?: number },
) => void;

type CenterableNode = {
  position?: { x?: number; y?: number };
  measured?: { width?: number; height?: number };
  width?: number;
  height?: number;
};

/**
 * Center the viewport on a node. Shared so every "focus this node" entry point
 * (elements panel, queue, auto-run, node context menu, …) computes the target
 * point identically.
 *
 * - `anchor: 'center'` (default) centers on the node's box center.
 * - `anchor: 'top'` centers horizontally but aligns to the node's top edge.
 * - `zoom` omitted keeps the current zoom (ReactFlow default).
 */
export function centerViewportOnNode(
  setCenter: SetCenterFn,
  node: CenterableNode | null | undefined,
  opts?: { anchor?: 'center' | 'top'; zoom?: number; duration?: number },
): void {
  if (!node) return;
  const w = node.measured?.width ?? node.width ?? 0;
  const h = node.measured?.height ?? node.height ?? 0;
  const cx = (node.position?.x ?? 0) + w / 2;
  const cy = (node.position?.y ?? 0) + (opts?.anchor === 'top' ? 0 : h / 2);
  setCenter(cx, cy, {
    duration: opts?.duration,
    ...(opts?.zoom != null ? { zoom: opts.zoom } : {}),
  });
}

export function calculateCanvasCenter(getViewport: GetViewportFn): { x: number; y: number } {
  const viewport = getViewport();
  const centerX = -viewport.x / viewport.zoom + window.innerWidth / 2 / viewport.zoom;
  const centerY = -viewport.y / viewport.zoom + window.innerHeight / 2 / viewport.zoom;
  return { x: centerX, y: centerY };
}

export function addNodeWithAutoPosition(params: {
  addNode: AddNodeFn;
  getViewport: GetViewportFn;
  type: string;
  data?: Record<string, unknown>;
  position?: { x: number; y: number };
}): string {
  const { addNode, getViewport, type, data, position } = params;
  const finalPosition = position ?? calculateCanvasCenter(getViewport);
  return addNode(type, {
    position: finalPosition,
    data: data ?? {},
  });
}

export function quickConnectMultiple(params: {
  selectedNodes: Array<{ id: string; position?: { x: number; y: number }; measured?: { width?: number } }>;
  addNode: AddNodeFn;
  setEdges: (updater: (eds: any[]) => any[]) => void;
  targetType: 'text' | 'image' | 'audio' | 'video' | 'stickyNote';
  gap?: number;
}): string[] {
  const { selectedNodes, addNode, setEdges, targetType, gap = 120 } = params;
  if (!Array.isArray(selectedNodes) || selectedNodes.length === 0) return [];
  const newEdges: any[] = [];
  const createdIds: string[] = [];
  for (const n of selectedNodes) {
    const width = n?.measured?.width ?? 300;
    const pos = n?.position ?? { x: 0, y: 0 };
    const position = { x: pos.x + width + gap, y: pos.y };
    // 对于 text 类型，设置为 transform 模式（文本模型节点）
    const nodeData = targetType === 'text' ? { viewMode: 'transform' } : {};
    const newId = addNode(targetType, { position, selected: true, data: nodeData });
    createdIds.push(String(newId));
    newEdges.push({ id: crypto.randomUUID ? crypto.randomUUID() : String(Math.random()), source: String(n.id), target: String(newId), type: 'animated' });
  }
  setEdges((eds: any[]) => eds.concat(newEdges));
  return createdIds;
}

export function quickConnectManyToOne(params: {
  selectedNodes: Array<{ id: string; position?: { x: number; y: number }; measured?: { width?: number; height?: number } }>;
  addNode: AddNodeFn;
  setEdges: (updater: (eds: any[]) => any[]) => void;
  targetType: 'image' | 'video';
  gap?: number;
}): string | null {
  const { selectedNodes, addNode, setEdges, targetType, gap = 120 } = params;
  if (!Array.isArray(selectedNodes) || selectedNodes.length === 0) return null;

  let maxRight = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const n of selectedNodes) {
    const pos = n?.position ?? { x: 0, y: 0 };
    const w = n?.measured?.width ?? 300;
    const h = n?.measured?.height ?? 240;
    maxRight = Math.max(maxRight, pos.x + w);
    // origin is [0, 0.5]: pos.y is the vertical center of the node
    minY = Math.min(minY, pos.y - h * 0.5);
    maxY = Math.max(maxY, pos.y + h * 0.5);
  }

  const centerY = (minY + maxY) / 2;
  const position = { x: maxRight + gap, y: centerY };

  const newId = addNode(targetType, { position, selected: true, data: {} });

  const newEdges = selectedNodes.map((n) => ({
    id: crypto.randomUUID ? crypto.randomUUID() : String(Math.random()),
    source: String(n.id),
    target: String(newId),
    type: 'animated',
  }));
  setEdges((eds: any[]) => eds.concat(newEdges));

  return String(newId);
}

export function deleteSelectedNodes(params: {
  nodes: Array<{ id: string; selected?: boolean }>;
  setNodes: (updater: (nds: any[]) => any[]) => void;
  setEdges: (updater: (eds: any[]) => any[]) => void;
  save?: () => void;
}) {
  const { nodes, setNodes, setEdges, save } = params;
  const selectedIds = new Set<string>((nodes || []).filter((n) => (n as any).selected).map((n) => String(n.id)));
  if (!selectedIds.size) return;
  setNodes((nds: any[]) => nds.filter((n) => !selectedIds.has(String(n.id))));
  setEdges((eds: any[]) => eds.filter((e) => !selectedIds.has(String((e as any).source)) && !selectedIds.has(String((e as any).target))));
  try { save?.(); } catch {}
}
